// ============================================
// CHÁMOMILE - Order Service
// Firebase Order Management
// Version 2.2 - FIXED: Uses settings/orderCounter for order numbers
// ============================================

import { db, storage } from '../config/firebase';
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  setDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  runTransaction,
  Timestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Check if Firebase is configured
const isFirebaseConfigured = (): boolean => {
  return db !== null && typeof db !== 'undefined';
};

// ============================================
// ORDER NUMBER MANAGEMENT (ATOMIC COUNTER)
// ============================================

/**
 * Get next order number using atomic counter
 * Uses Firestore transaction to ensure no duplicate numbers
 * 
 * FIXED: Now uses settings/orderCounter document with onlineOrderCount field
 * to match the Firestore structure used by the POS system
 */
export const getNextOrderNumber = async (): Promise<string> => {
  if (!isFirebaseConfigured() || !db) {
    // Fallback for non-Firebase environment
    try {
      const existingOrders = JSON.parse(localStorage.getItem('chamomile-online-orders') || '[]');
      const nextNumber = existingOrders.length + 1;
      return `#ONL-${String(nextNumber).padStart(4, '0')}`;
    } catch {
      return '#ONL-0001';
    }
  }

  try {
    // FIXED: Changed from 'counters/online-orders' to 'settings/orderCounter'
    const counterRef = doc(db, 'settings', 'orderCounter');

    // Use transaction to ensure atomic increment
    const newNumber = await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);

      let currentCount = 0;
      if (counterDoc.exists()) {
        // FIXED: Use onlineOrderCount field instead of count
        currentCount = counterDoc.data().onlineOrderCount || 0;
      }

      // Online order sequence starts from 1 (if currentCount is 0, next is 1)
      const nextCount = currentCount + 1;
      
      console.log(`[Counter] Current: ${currentCount}, Next: ${nextCount}`);

      // Update or create counter document
      // FIXED: Update the onlineOrderCount field
      transaction.set(counterRef, {
        onlineOrderCount: nextCount,
        lastUpdated: serverTimestamp(),
        prefix: 'ONL'
      }, { merge: true });

      return nextCount;
    });

    return `#ONL-${String(newNumber).padStart(4, '0')}`;
  } catch (error) {
    console.error('❌ Error getting next order number:', error);

    // Fallback: count existing documents
    try {
      const ordersRef = collection(db, 'online-orders');
      const querySnapshot = await getDocs(ordersRef);
      const nextNumber = querySnapshot.size + 1;
      return `#ONL-${String(nextNumber).padStart(4, '0')}`;
    } catch {
      return '#ONL-0001';
    }
  }
};

/**
 * Initialize counter if not exists
 * Call this once during app setup
 * 
 * FIXED: Now initializes in settings/orderCounter
 */
export const initializeOrderCounter = async (): Promise<void> => {
  if (!isFirebaseConfigured() || !db) return;

  try {
    const counterRef = doc(db, 'settings', 'orderCounter');
    const counterDoc = await getDoc(counterRef);

    if (!counterDoc.exists() || counterDoc.data().onlineOrderCount === undefined) {
      // Count existing orders to set initial counter
      const ordersRef = collection(db, 'online-orders');
      const querySnapshot = await getDocs(ordersRef);
      const currentCount = querySnapshot.size;

      await setDoc(counterRef, {
        onlineOrderCount: currentCount,
        lastUpdated: serverTimestamp(),
        prefix: 'ONL',
        initialized: true
      }, { merge: true });

      console.log(`✅ Order counter initialized at ${currentCount}`);
    }
  } catch (error) {
    console.error('❌ Error initializing counter:', error);
  }
};

/**
 * Sync counter with actual order count
 * Use this to fix desync issues
 * 
 * FIXED: Now syncs settings/orderCounter
 */
export const syncOrderCounter = async (): Promise<number> => {
  if (!isFirebaseConfigured() || !db) return 0;

  try {
    // Count actual orders
    const ordersRef = collection(db, 'online-orders');
    const querySnapshot = await getDocs(ordersRef);
    const actualCount = querySnapshot.size;

    // Also check orders collection for online orders
    const posOrdersRef = collection(db, 'orders');
    const posQuery = query(posOrdersRef, where('source', '==', 'online'));
    const posSnapshot = await getDocs(posQuery);
    const posOnlineCount = posSnapshot.size;

    // Use the higher count
    const maxCount = Math.max(actualCount, posOnlineCount);

    // Update counter - FIXED: Use settings/orderCounter
    const counterRef = doc(db, 'settings', 'orderCounter');
    await setDoc(counterRef, {
      onlineOrderCount: maxCount,
      lastUpdated: serverTimestamp(),
      lastSync: serverTimestamp(),
      prefix: 'ONL'
    }, { merge: true });

    console.log(`✅ Counter synced to ${maxCount}`);
    return maxCount;
  } catch (error) {
    console.error('❌ Error syncing counter:', error);
    return 0;
  }
};

// ============================================
// ORDER DATA TYPES
// ============================================

export interface OrderItem {
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

export interface CustomerInfo {
  name: string;
  phone: string;
  email?: string;
}

export interface OnlineOrder {
  orderId: string;
  orderNumber?: string;  // Sequential order number like #ONL-0001
  items: OrderItem[];
  totalBottles: number;
  subtotal: number;
  discount: number;
  discountTier: string | null;
  promoCode: string | null;
  promoDiscount: number;
  total: number;
  deliveryMethod: string;
  scheduledDate: string;
  scheduledTime: string;
  customerInfo: CustomerInfo;
  paymentReceiptUrl: string | null;
  status: string;
  createdAt?: any;
  updatedAt?: any;
  // Review request fields
  reviewRequestStatus?: string;
  reviewRequestHistory?: any[];
  reviewRequestLastSent?: any;
  reviewRequestCompletedAt?: any;
  collectionDate?: any;
}

// ============================================
// DISCOUNT CALCULATION
// ============================================

export const calculateDiscount = (cart: Record<string, number>): { amount: number; tier: 'Starter' | 'Premium' | null } => {
  const totalBottles = Object.values(cart).reduce((sum, qty) => sum + qty, 0);
  const flavourCount = Object.keys(cart).length;

  // Premium Pack: 20+ bottles, 4+ flavours → RM4/bottle
  if (totalBottles >= 20 && flavourCount >= 4) {
    return { amount: totalBottles * 4, tier: 'Premium' };
  }

  // Starter Pack: 10+ bottles, 2+ flavours → RM2/bottle  
  if (totalBottles >= 10 && flavourCount >= 2) {
    return { amount: totalBottles * 2, tier: 'Starter' };
  }

  return { amount: 0, tier: null };
};

// ============================================
// ORDER MANAGEMENT
// ============================================

/**
 * Save order to Firebase
 */
export const saveOrder = async (orderData: OnlineOrder): Promise<string | null> => {
  if (!isFirebaseConfigured() || !db) {
    console.log('⚠️ Firebase not configured, saving to localStorage');
    try {
      const orders = JSON.parse(localStorage.getItem('chamomile-online-orders') || '[]');
      orders.push({ ...orderData, createdAt: new Date().toISOString() });
      localStorage.setItem('chamomile-online-orders', JSON.stringify(orders));
      return orderData.orderId;
    } catch (e) {
      console.warn('localStorage error:', e);
      return null;
    }
  }

  try {
    const docRef = await addDoc(collection(db, 'online-orders'), {
      ...orderData,
      source: 'online', // Add source field for consistency
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    console.log('✅ Order saved to Firestore:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ Firestore save error:', error);
    return null;
  }
};

/**
 * Get all online orders (for POS)
 */
export const getOnlineOrders = async (daysBack: number = 30): Promise<OnlineOrder[]> => {
  if (!isFirebaseConfigured() || !db) {
    try {
      return JSON.parse(localStorage.getItem('chamomile-online-orders') || '[]');
    } catch {
      return [];
    }
  }

  try {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysBack);

    const ordersRef = collection(db, 'online-orders');
    const q = query(
      ordersRef,
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc, index) => ({
      id: doc.id,
      ...doc.data(),
      // Generate order number if missing
      orderNumber: doc.data().orderNumber || `#ONL-${String(index + 1).padStart(4, '0')}`
    })) as unknown as OnlineOrder[];
  } catch (error) {
    console.error('❌ Error fetching orders:', error);
    return [];
  }
};

/**
 * Get orders for review requests (last 14 days with collection date)
 */
export const getOrdersForReviewRequests = async (): Promise<OnlineOrder[]> => {
  if (!isFirebaseConfigured() || !db) {
    return [];
  }

  try {
    const fourteenDaysAgo = new Date();
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);

    const ordersRef = collection(db, 'online-orders');
    const q = query(
      ordersRef,
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const orders = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as unknown as OnlineOrder[];

    // Filter to orders within last 14 days
    return orders.filter(order => {
      const orderDate = order.createdAt?.toDate ? order.createdAt.toDate() : new Date(order.createdAt);
      return orderDate >= fourteenDaysAgo;
    });
  } catch (error) {
    console.error('❌ Error fetching orders for reviews:', error);
    return [];
  }
};

/**
 * Get order position (for display)
 */
export const getOrderPosition = async (orderId: string): Promise<string> => {
  if (!isFirebaseConfigured() || !db) {
    return '#ONL-0001';
  }

  try {
    const ordersRef = collection(db, 'online-orders');
    const q = query(ordersRef, orderBy('createdAt', 'asc'));
    const querySnapshot = await getDocs(q);

    let position = 1;
    for (const doc of querySnapshot.docs) {
      if (doc.data().orderId === orderId || doc.id === orderId) {
        break;
      }
      position++;
    }

    return `#ONL-${String(position).padStart(4, '0')}`;
  } catch (error) {
    console.error('❌ Error getting order position:', error);
    return '#ONL-0001';
  }
};

// ============================================
// RECEIPT UPLOAD
// ============================================

export const uploadReceipt = async (file: File, orderId: string): Promise<string | null> => {
  if (!storage) {
    console.warn('⚠️ Firebase Storage not configured');
    return null;
  }

  try {
    const timestamp = Date.now();
    const ext = file.name.split('.').pop() || 'jpg';
    const path = `receipts/${orderId}_${timestamp}.${ext}`;
    const storageRef = ref(storage, path);

    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);

    console.log('✅ Receipt uploaded:', url);
    return url;
  } catch (error) {
    console.error('❌ Upload error:', error);
    return null;
  }
};

// ============================================
// EXPORTS
// ============================================

export {
  isFirebaseConfigured
};

export default {
  getNextOrderNumber,
  initializeOrderCounter,
  syncOrderCounter,
  calculateDiscount,
  saveOrder,
  getOnlineOrders,
  getOrdersForReviewRequests,
  getOrderPosition,
  uploadReceipt,
  isFirebaseConfigured
};
